import React, { useState, useMemo } from 'react';
import { ChevronRight, Menu, X } from 'lucide-react';

const Navbar = ({ company, setView, activeView, onBack }) => {
  const [isOpen, setIsOpen] = useState(false);

  const theme = useMemo(() => {
    // We use the ID from your companies.js because it is consistent
    const companyId = company?.id || "";

    // 1. SPICES - Light Green
    if (companyId === 'spices') {
      return {
        active: 'bg-gradient-to-r from-emerald-500 to-green-600 text-white shadow-green-500/25',
        hover: 'hover:bg-green-50/50 hover:text-green-700 hover:shadow-green-200/50 hover:border-green-200',
        border: 'border-green-100/50',
        text: 'from-slate-900 via-emerald-900 to-green-900',
        buttonHover: 'hover:bg-green-50/80 hover:text-green-600 hover:shadow-green-200/50'
      };
    }

    // 2. COMMODITIES - Light Blue
    if (companyId === 'commodities') {
      return {
        active: 'bg-gradient-to-r from-sky-400 to-blue-500 text-white shadow-blue-500/25',
        hover: 'hover:bg-sky-50/50 hover:text-sky-700 hover:shadow-sky-200/50 hover:border-sky-200',
        border: 'border-sky-100/50',
        text: 'from-slate-900 via-blue-900 to-sky-900',
        buttonHover: 'hover:bg-sky-50/80 hover:text-sky-600 hover:shadow-sky-200/50'
      };
    }

    // 3. APPAREL - Black and Silver
    if (companyId === 'apparel') {
      return {
        active: 'bg-gradient-to-r from-slate-800 to-black text-white shadow-slate-500/25',
        hover: 'hover:bg-slate-100/50 hover:text-black hover:shadow-slate-300/50 hover:border-slate-300',
        border: 'border-slate-200/50',
        text: 'from-black via-slate-700 to-slate-400', 
        buttonHover: 'hover:bg-slate-100/80 hover:text-black hover:shadow-slate-200/50'
      };
    }

    // Default Fallback
    return {
      active: 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white',
      hover: 'hover:bg-blue-50',
      border: 'border-blue-100',
      text: 'from-slate-900 to-blue-900',
      buttonHover: 'hover:bg-blue-50'
    };
  }, [company.id]); // Dependency on ID is more reliable than Name

  const NavLink = ({ name, view }) => (
    <button 
      onClick={() => { setView(view); setIsOpen(false); }}
      className={`px-6 py-3 rounded-xl font-black transition-all duration-300 shadow-md backdrop-blur-sm border ${
        activeView === view 
          ? theme.active 
          : `text-slate-700 border-slate-100/50 ${theme.hover}`
      }`}
    >
      {name.charAt(0).toUpperCase() + name.slice(1)}
    </button>
  );

  return (
    <nav className={`sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b-2 px-6 py-4 shadow-lg transition-all duration-500 ${theme.border}`}>
      <div className="max-w-7xl mx-auto flex justify-between items-center">
        <div className="flex items-center gap-4">
          <button 
            onClick={onBack} 
            className={`p-3 rounded-2xl transition-all duration-300 text-slate-700 shadow-sm hover:scale-105 ${theme.buttonHover}`}
          >
            <ChevronRight className="rotate-180" size={24} />
          </button>
          <h1 className={`text-2xl font-black bg-gradient-to-r ${theme.text} bg-clip-text text-transparent uppercase tracking-tight`}>
            {company.name}
          </h1>
        </div>

        <div className="hidden md:flex gap-2">
          <NavLink name="home" view="home" />
          <NavLink name="about" view="about" />
          <NavLink name="services" view="services" />
          <NavLink name="contact" view="contact" />
        </div>

        <button 
          className={`md:hidden p-2 rounded-2xl transition-all duration-300 shadow-sm ${theme.buttonHover}`}
          onClick={() => setIsOpen(!isOpen)}
        >
          {isOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {isOpen && (
        <div className={`md:hidden absolute top-full left-0 w-full bg-white/95 backdrop-blur-xl border-b-2 p-6 flex flex-col gap-4 shadow-2xl border-t ${theme.border}`}>
          <NavLink name="home" view="home" />
          <NavLink name="about" view="about" />
          <NavLink name="services" view="services" />
          <NavLink name="contact" view="contact" />
        </div>
      )}
    </nav>
  );
};

export default Navbar;